export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  componentName?: string;
  userAction?: string;
  url?: string;
  userAgent?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  resolved: boolean;
  affectedUsers: number;
}

export interface PerformanceEntry {
  timestamp: string;
  lcp?: number;
  fid?: number;
  cls?: number;
  ttfb?: number;
  pageLoad?: number;
}

export interface UptimeEntry {
  timestamp: string;
  status: 'up' | 'down' | 'degraded';
  responseTime: number;
}

const ERROR_LOG_KEY = 'dff_error_logs';
const PERF_LOG_KEY = 'dff_perf_logs';
const UPTIME_LOG_KEY = 'dff_uptime_logs';
const USER_ACTIONS_KEY = 'dff_user_actions';
const MAX_LOGS = 200;

function generateId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

function getStoredLogs<T>(key: string): T[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function storeLogs<T>(key: string, logs: T[]): void {
  if (typeof window === 'undefined') return;
  try {
    const trimmed = logs.slice(-MAX_LOGS);
    localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {
    // Storage full or unavailable
  }
}

export const ErrorLogger = {
  log(
    error: Error | string,
    options: {
      componentName?: string;
      userAction?: string;
      severity?: ErrorLogEntry['severity'];
    } = {}
  ): ErrorLogEntry {
    const message = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'string' ? undefined : error.stack;

    const entry: ErrorLogEntry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      message,
      stack,
      componentName: options.componentName,
      userAction: options.userAction || ErrorLogger.getLastUserAction(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      severity: options.severity || 'medium',
      resolved: false,
      affectedUsers: 1,
    };

    const logs = getStoredLogs<ErrorLogEntry>(ERROR_LOG_KEY);
    logs.push(entry);
    storeLogs(ERROR_LOG_KEY, logs);

    // Also log to console for dev visibility
    console.error(`[ErrorLogger] ${entry.componentName || 'Unknown'}: ${message}`, { stack, userAction: entry.userAction });

    return entry;
  },

  trackUserAction(action: string): void {
    if (typeof window === 'undefined') return;
    try {
      const actions = getStoredLogs<{ action: string; timestamp: string }>(USER_ACTIONS_KEY);
      actions.push({ action, timestamp: new Date().toISOString() });
      storeLogs(USER_ACTIONS_KEY, actions);
    } catch {
      // ignore
    }
  },

  getLastUserAction(): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const actions = getStoredLogs<{ action: string; timestamp: string }>(USER_ACTIONS_KEY);
    return actions.length > 0 ? actions[actions.length - 1].action : undefined;
  },

  getLogs(hours = 24): ErrorLogEntry[] {
    const logs = getStoredLogs<ErrorLogEntry>(ERROR_LOG_KEY);
    const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    return logs.filter((l) => l.timestamp >= cutoff);
  },

  getAllLogs(): ErrorLogEntry[] {
    return getStoredLogs<ErrorLogEntry>(ERROR_LOG_KEY);
  },

  resolveError(id: string): void {
    const logs = getStoredLogs<ErrorLogEntry>(ERROR_LOG_KEY);
    const updated = logs.map((l) => (l.id === id ? { ...l, resolved: true } : l));
    storeLogs(ERROR_LOG_KEY, updated);
  },

  clearLogs(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(ERROR_LOG_KEY);
  },

  recordPerformance(metrics: Omit<PerformanceEntry, 'timestamp'>): void {
    const entry: PerformanceEntry = {
      timestamp: new Date().toISOString(),
      ...metrics,
    };
    const logs = getStoredLogs<PerformanceEntry>(PERF_LOG_KEY);
    logs.push(entry);
    storeLogs(PERF_LOG_KEY, logs);
  },

  getPerformanceLogs(hours = 24): PerformanceEntry[] {
    const logs = getStoredLogs<PerformanceEntry>(PERF_LOG_KEY);
    const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    return logs.filter((l) => l.timestamp >= cutoff);
  },

  recordUptime(status: UptimeEntry['status'], responseTime: number): void {
    const entry: UptimeEntry = {
      timestamp: new Date().toISOString(),
      status,
      responseTime,
    };
    const logs = getStoredLogs<UptimeEntry>(UPTIME_LOG_KEY);
    logs.push(entry);
    storeLogs(UPTIME_LOG_KEY, logs);
  },

  getUptimeLogs(hours = 24): UptimeEntry[] {
    const logs = getStoredLogs<UptimeEntry>(UPTIME_LOG_KEY);
    const cutoff = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    return logs.filter((l) => l.timestamp >= cutoff);
  },

  getStats(hours = 24) {
    const errors = ErrorLogger.getLogs(hours);
    const uptime = ErrorLogger.getUptimeLogs(hours);
    const perf = ErrorLogger.getPerformanceLogs(hours);

    const totalErrors = errors.length;
    const criticalErrors = errors.filter((e) => e.severity === 'critical').length;
    const unresolvedErrors = errors.filter((e) => !e.resolved).length;
    const affectedUsers = errors.reduce((sum, e) => sum + e.affectedUsers, 0);

    const uptimePercent =
      uptime.length > 0
        ? (uptime.filter((u) => u.status === 'up').length / uptime.length) * 100
        : 100;

    const avgResponseTime =
      uptime.length > 0
        ? uptime.reduce((sum, u) => sum + u.responseTime, 0) / uptime.length
        : 0;

    const avgLcp =
      perf.filter((p) => p.lcp).length > 0
        ? perf.reduce((sum, p) => sum + (p.lcp || 0), 0) / perf.filter((p) => p.lcp).length
        : 0;

    return {
      totalErrors,
      criticalErrors,
      unresolvedErrors,
      affectedUsers,
      uptimePercent: Math.round(uptimePercent * 100) / 100,
      avgResponseTime: Math.round(avgResponseTime),
      avgLcp: Math.round(avgLcp),
      errorsByComponent: errors.reduce(
        (acc, e) => {
          const key = e.componentName || 'Unknown';
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
      errorsBySeverity: errors.reduce(
        (acc, e) => {
          acc[e.severity] = (acc[e.severity] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      ),
    };
  },

  // Seed some demo data for the monitoring dashboard
  seedDemoData(): void {
    if (typeof window === 'undefined') return;
    const existing = getStoredLogs<ErrorLogEntry>(ERROR_LOG_KEY);
    if (existing.length > 5) return; // already has data

    const now = Date.now();
    const components = ['SearchWidget', 'PersonalizedDeals', 'TrustSignals', 'HeroSection', 'Header'];
    const actions = ['search_submit', 'tab_switch', 'carousel_scroll', 'form_input', 'page_load'];
    const severities: ErrorLogEntry['severity'][] = ['low', 'medium', 'high', 'critical'];
    const messages = [
      'Cannot read properties of undefined',
      'Network request failed',
      'Hydration mismatch detected',
      'Timeout exceeded for API call',
      'Invalid prop type received',
    ];

    const demoErrors: ErrorLogEntry[] = Array.from({ length: 18 }, (_, i) => ({
      id: generateId(),
      timestamp: new Date(now - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
      message: messages[i % messages.length],
      stack: `Error: ${messages[i % messages.length]}\n    at Component (${components[i % components.length]}.tsx:${Math.floor(Math.random() * 100) + 10})`,
      componentName: components[i % components.length],
      userAction: actions[i % actions.length],
      url: 'https://travelgate7783.builtwithrocket.new/homepage',
      severity: severities[i % severities.length],
      resolved: i % 3 === 0,
      affectedUsers: Math.floor(Math.random() * 5) + 1,
    }));

    storeLogs(ERROR_LOG_KEY, demoErrors);

    // Seed uptime data
    const demoUptime: UptimeEntry[] = Array.from({ length: 48 }, (_, i) => ({
      timestamp: new Date(now - i * 30 * 60 * 1000).toISOString(),
      status: i === 5 || i === 22 ? 'degraded' : 'up',
      responseTime: 150 + Math.floor(Math.random() * 200),
    }));
    storeLogs(UPTIME_LOG_KEY, demoUptime);

    // Seed performance data
    const demoPerf: PerformanceEntry[] = Array.from({ length: 24 }, (_, i) => ({
      timestamp: new Date(now - i * 60 * 60 * 1000).toISOString(),
      lcp: 1800 + Math.floor(Math.random() * 1200),
      fid: 50 + Math.floor(Math.random() * 100),
      cls: Math.random() * 0.2,
      ttfb: 200 + Math.floor(Math.random() * 300),
      pageLoad: 2000 + Math.floor(Math.random() * 2000),
    }));
    storeLogs(PERF_LOG_KEY, demoPerf);
  },
};

export default ErrorLogger;
